import { createServerFn } from "@tanstack/react-start";

import type {
  CoachSelect,
  CertificationsSelect,
  TestimonialSelect,
  OfferSelect,
  CoachingResultsSelect,
  OfferTagSelect,
  OfferBenefitSelect,
} from "~/../drizzle/schema";
import type { CoachDetailsSelect, ProfileSelect } from "~/../drizzle/schema";
import type { ResourceSelect } from "~/../drizzle/schema";
import { ClaimVerificationState } from "~/lib/schemas/db-enums";
import { ContentStatus } from "~/lib/schemas/db-enums-cih";
import {
  toDecider,
  type CredentialVerification,
} from "~/lib/shared/cco-claims";
import type {
  CoachActiveSummitWorkshop,
  SummitForSpecialOffer,
} from "~/lib/summit";

type Offer = OfferSelect & {
  tags: OfferTagSelect[];
  benefits: OfferBenefitSelect[];
};

// Public coach profile - excludes all sensitive/internal fields
export type CoachPublicProfile = Omit<
  CoachSelect,
  | "ghlContactId"
  | "ghlLocationId"
  | "ghlUserId"
  | "clerkUserId"
  | "stripeCustomerId"
  | "stripeAccountId"
  | "stripeSubscriptionId"
  | "stripePayoutsEnabled"
  | "stripeChargesEnabled"
  | "contractSigned"
  | "createdAt"
  | "updatedAt"
  | "email"
  | "phone"
>;

// Internal coach data with all fields (for backend/authenticated use)
export type CoachPrivateData = CoachSelect & {
  profile: ProfileSelect | null;
  details: CoachDetailsSelect | null;
  resources: ResourceSelect[];
  certifications: CertificationsSelect[];
  testimonials: TestimonialSelect[];
  results: CoachingResultsSelect[];
  mainOffer: Offer | null;
  activeSummitWorkshops: CoachActiveSummitWorkshop[];
  summitOfferSummits: SummitForSpecialOffer[];
};

type ClaimVerificationFields = {
  verificationState: ClaimVerificationState;
  decidedByCcoId: string | null;
  verificationDate: string | null;
};

function toVerification(
  claim: ClaimVerificationFields,
  organization: string,
): CredentialVerification | null {
  if (claim.verificationState !== ClaimVerificationState.Verified) return null;
  return {
    decider: toDecider(claim.decidedByCcoId, organization),
    date: claim.verificationDate,
  };
}

/** CCO membership data for public profile display. */
export type CcoMembership = {
  ccoId: string;
  ccoName: string;
  ccoSlug: string;
  ccoLogoUrl: string | null;
  /** False when the organization page 404s (draft or archived), so no link. */
  isPublished: boolean;
  verification: CredentialVerification | null;
};

/** CTP certification data for public profile display. */
export type CtpCertification = {
  ctpId: string;
  ctpName: string;
  ctpSlug: string;
  ctpLogoUrl: string | null;
  ccoName: string;
  ccoSlug: string;
  ccoLogoUrl: string | null;
  certificationDate: Date | null;
  /** False when the program page 404s (draft or archived), so no link. */
  isPublished: boolean;
  verification: CredentialVerification | null;
};

// Public coach data for public-facing pages
export type CoachPublicData = CoachPublicProfile & {
  profile: ProfileSelect;
  resources: ResourceSelect[];
  certifications: CertificationsSelect[];
  ctpCertifications?: CtpCertification[];
  ccoMemberships?: CcoMembership[];
  testimonials: TestimonialSelect[];
  results: CoachingResultsSelect[];
  mainOffer: Offer | null;
};

// Legacy types for backwards compatibility - prefer CoachPublicData and CoachPrivateData
export type CoachData = CoachPrivateData;
export type CoachPublicProfileData = CoachPublicData;

type PublicationFields = { status: string; draftOf: string | null };

/** Same rule the public pages use: the live row of a published record. */
export function isPublishedRecord(record: PublicationFields): boolean {
  return record.status === ContentStatus.Published && record.draftOf === null;
}

/** Transform CTP relations from coach query into flat certification objects for display. */
function transformCtpCertifications(
  ctps:
    | ({
        ctp:
          | (PublicationFields & {
              id: string;
              name: string;
              slug: string | null;
              logoUrl: string | null;
              cco: {
                name: string;
                slug: string | null;
                logoUrl: string | null;
              } | null;
            })
          | null;
        certificationDate: Date | null;
      } & ClaimVerificationFields)[]
    | undefined,
): CtpCertification[] {
  const result: CtpCertification[] = [];
  for (const ct of ctps ?? []) {
    const ctp = ct.ctp;
    const cco = ctp?.cco;
    if (!ctp || !cco || !ctp.slug || !cco.slug) continue;
    result.push({
      ctpId: ctp.id,
      ctpName: ctp.name,
      ctpSlug: ctp.slug,
      ctpLogoUrl: ctp.logoUrl,
      ccoName: cco.name,
      ccoSlug: cco.slug,
      ccoLogoUrl: cco.logoUrl,
      certificationDate: ct.certificationDate,
      isPublished: isPublishedRecord(ctp),
      verification: toVerification(ct, cco.name),
    });
  }
  return result;
}

/** Transform CCO relations from coach query into flat membership objects for display. */
function transformCcoMemberships(
  ccos:
    | ({
        cco:
          | (PublicationFields & {
              id: string;
              name: string;
              slug: string | null;
              logoUrl: string | null;
            })
          | null;
      } & ClaimVerificationFields)[]
    | undefined,
): CcoMembership[] {
  const result: CcoMembership[] = [];
  for (const cc of ccos ?? []) {
    const cco = cc.cco;
    if (!cco || !cco.slug) continue;
    result.push({
      ccoId: cco.id,
      ccoName: cco.name,
      ccoSlug: cco.slug,
      ccoLogoUrl: cco.logoUrl,
      isPublished: isPublishedRecord(cco),
      verification: toVerification(cc, cco.name),
    });
  }
  return result;
}

export const getCoachData = createServerFn({ method: "GET" })
  .validator((data: { username: string }) => data)
  .handler(async ({ data }) => {
    const { username } = data;
    // 404s unclaimed pre-generated profiles for everyone but the coach —
    // see ~/lib/server/coach-visibility (issue #1014).
    const { getPublicCoachByUsername } =
      await import("~/lib/server/coach-visibility");
    const coach = await getPublicCoachByUsername(username);

    return {
      ...coach,
      ctpCertifications: transformCtpCertifications(coach.ctps),
      ccoMemberships: transformCcoMemberships(coach.ccos),
    } as CoachPublicData;
  });

export const getOtherOffers = createServerFn({ method: "GET" })
  .validator((data: { coachId: string }) => data)
  .handler(async ({ data }) => {
    // Plan, main offer, and visibility are derived server-side so a hidden
    // pre-generated coach's offers can't be fetched by UUID (issue #1014).
    const { getPublicOtherOffers } =
      await import("~/lib/server/coach-visibility");
    return getPublicOtherOffers(data.coachId);
  });

export const getOfferByUsernameAndId = createServerFn({ method: "GET" })
  .validator((data: { username: string; offerId: string }) => data)
  .handler(async ({ data }) => {
    // Coach visibility and the offer lifecycle are both decided in
    // ~/lib/server/coach-visibility.
    const { getPublicOfferByUsernameAndId } =
      await import("~/lib/server/coach-visibility");
    const result = await getPublicOfferByUsernameAndId(
      data.username,
      data.offerId,
    );
    if (result.kind !== "found") {
      return result;
    }

    return {
      kind: result.kind,
      coach: {
        ...result.coach,
        ctpCertifications: transformCtpCertifications(result.coach.ctps),
      } as CoachPublicData,
      offer: result.offer,
    };
  });
